/**
 * `/admin/input-data` — ручной ввод и обновление показателей паспорта.
 *
 * Состав формы страница НЕ знает: она запрашивает его у сервера
 * (`/api/supervisor/passport-form`), который собирает форму из реестра
 * показателей — того же, что питает Excel-шаблон. Поэтому подписи, единицы,
 * состав по уровню территории и адреса записи всегда совпадают между двумя
 * способами ввода, а страница остаётся тонкой: разделы, фильтры, правки,
 * сохранение.
 *
 * Кто может править: супер-администратор (любая территория) и ответственное
 * лицо КАТО (только свои). Проверка — на сервере; здесь UX-слой, который просто
 * не показывает лишнего.
 *
 * Показателей много, поэтому: слева разделы со счётчиком заполненности, справа
 * карточки одного раздела, сверху поиск и фильтр «только незаполненные», снизу
 * панель с числом правок. Уйти со страницы с несохранёнными правками браузер не
 * даст молча.
 */
import { Check, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { useUser } from '../../../contexts/UserContext';
import { cn } from '../../../lib/cn';
import { canEditData, katoCovers } from '../../../lib/user-access';
import {
  getPassportForm,
  savePassportForm,
  setSourceMode,
  type PassportField,
  type PassportForm,
  type PassportSection,
} from '../../../requests/input_data.request';
import { getKatoList, type KatoListItem } from '../../../requests/public.request';
import { AkimPhotoCard } from './components/AkimPhotoCard';
import { BlockCard } from './components/BlockCard';
import { FieldsCard } from './components/FieldsCard';
import { TemplatePanel } from './components/TemplatePanel';
import {
  buildState,
  changedBlocks,
  dirtyCount,
  emptyRow,
  pick,
  sectionProgress,
  toSaveRequest,
  type FormState,
  type Lang,
} from './formState';
import { manualKeysOf } from './sources';

/** Сколько лет можно выбрать назад от текущего. */
const YEARS_BACK = 4;

/**
 * Единственное поле-изображение, известное ДО появления `is_image` в контракте.
 * Нужен, пока в проде может стоять бэкенд без этого признака: репозитории
 * деплоятся порознь, и фронт обязан пережить старый бэкенд. Удалить, когда
 * `is_image` раскатан во всех регионах.
 */
const LEGACY_IMAGE_KEY = 'general.akim_image_id';

/**
 * Поле-изображение: в контракте это строковый показатель реестра (id блоба в
 * хранилище), но вводится он файлом — см. `AkimPhotoCard`, — поэтому из общего
 * списка вынимается и текстовым полем не показывается.
 *
 * Признак приходит с сервера (`is_image` в реестре показателей), а не узнаётся
 * по ключу: то же знание о поле фронт и бэкенд держали в трёх местах сразу, и
 * добавить второе изображение значило бы вписать его ключ во все (DP-461).
 *
 * На ключ откатываемся, только если сервер признака не прислал ВООБЩЕ. Новый
 * бэкенд шлёт его каждому полю, в том числе `false`, — тогда до ключа дело не
 * доходит и фолбэк сам себя отключает.
 */
const isImageField = (field: PassportField): boolean =>
  field.is_image ?? field.key === LEGACY_IMAGE_KEY;

const EMPTY_STATE: FormState = { scalars: {}, blocks: {} };

/** Плоский список территорий с отступом по глубине — иначе округа не различить. */
const flatten = (items: KatoListItem[]): { item: KatoListItem; depth: number }[] => {
  const children = new Map<number | null, KatoListItem[]>();
  for (const item of items) {
    const bucket = children.get(item.parent_id);
    if (bucket) bucket.push(item);
    else children.set(item.parent_id, [item]);
  }
  const out: { item: KatoListItem; depth: number }[] = [];
  const walk = (parent: number | null, depth: number) => {
    for (const item of children.get(parent) ?? []) {
      out.push({ item, depth });
      walk(item.kato, depth + 1);
    }
  };
  walk(null, 0);
  const known = new Set(items.map((i) => i.kato));
  for (const item of items) {
    if (item.parent_id !== null && !known.has(item.parent_id)) out.push({ item, depth: 0 });
  }
  return out;
};

export const InputData = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const lang: Lang = i18n.language === 'kz' ? 'kz' : 'ru';

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [kato, setKato] = useState<number | null>(null);
  const [katoItems, setKatoItems] = useState<KatoListItem[]>([]);
  const [form, setForm] = useState<PassportForm | null>(null);
  const [state, setState] = useState<FormState>(EMPTY_STATE);
  const [initial, setInitial] = useState<FormState>(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [query, setQuery] = useState('');
  const [emptyOnly, setEmptyOnly] = useState(false);
  const [manualKeys, setManualKeys] = useState<Set<string>>(() => new Set());

  const isSuperAdmin = user?.is_admin ?? false;
  const ownKatos = useMemo(
    () => (user?.kato_access ?? []).filter((code) => code !== 0),
    [user],
  );
  // Право ввода, а не наличие территории: «только просмотр» тоже имеет КАТО.
  const canEdit = canEditData(user);

  // Справочник территорий: супер-админу — весь, ответственному лицу — его КАТО
  // вместе с подчинённой иерархией (по остальным сервер всё равно откажет).
  // `ownKatos` — корни поддеревьев, поэтому сравнение через `includes` спрятало
  // бы от ответственного за район все сёла внутри этого района.
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    getKatoList()
      .then((response) => {
        if (cancelled) return;
        const items = isSuperAdmin
          ? response.items
          : response.items.filter((item) =>
            ownKatos.some((root) => katoCovers(root, item.kato)));
        setKatoItems(items);
        setKato((current) => current ?? items[0]?.kato ?? ownKatos[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setKato((current) => current ?? ownKatos[0] ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit, isSuperAdmin, ownKatos]);

  const reload = useCallback(async (targetKato: number, targetYear: number) => {
    setLoading(true);
    setFailed(false);
    try {
      const loaded = await getPassportForm(targetKato, targetYear);
      setForm(loaded);
      setState(buildState(loaded));
      // Отдельный снимок: сравнение «изменилось / нет» не должно видеть правки.
      setInitial(buildState(loaded));
      // Выбор источника приезжает вместе с формой — он про эту территорию и год.
      setManualKeys(manualKeysOf(loaded));
      setActiveSection(0);
    } catch {
      setFailed(true);
      setForm(null);
      setState(EMPTY_STATE);
      setInitial(EMPTY_STATE);
      setManualKeys(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (kato === null) return;
    void reload(kato, year);
  }, [kato, year, reload]);

  const dirty = useMemo(() => dirtyCount(state, initial), [state, initial]);

  useEffect(() => {
    if (dirty === 0) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // --- правки -----------------------------------------------------------
  const setScalar = (key: string, value: string) => {
    setState((current) => ({
      ...current,
      scalars: { ...current.scalars, [key]: value },
    }));
  };

  const setCell = (blockId: string, uid: string, column: string, value: string) => {
    setState((current) => ({
      ...current,
      blocks: {
        ...current.blocks,
        [blockId]: {
          rows: (current.blocks[blockId]?.rows ?? []).map((row) =>
            row.uid === uid ? { ...row, cells: { ...row.cells, [column]: value } } : row,
          ),
        },
      },
    }));
  };

  const addRow = (blockId: string) => {
    const block = form?.sections
      .flatMap((section) => section.blocks)
      .find((candidate) => candidate.id === blockId);
    if (block === undefined) return;
    setState((current) => ({
      ...current,
      blocks: {
        ...current.blocks,
        [blockId]: { rows: [...(current.blocks[blockId]?.rows ?? []), emptyRow(block)] },
      },
    }));
  };

  const removeRow = (blockId: string, uid: string) => {
    setState((current) => ({
      ...current,
      blocks: {
        ...current.blocks,
        [blockId]: {
          rows: (current.blocks[blockId]?.rows ?? []).filter((row) => row.uid !== uid),
        },
      },
    }));
  };

  /**
   * Взять значение ОДНОГО элемента формы из снимка: показателя либо блока.
   *
   * Ключ приходит один и тот же и для поля, и для блока (`field.key` / `block.id`)
   * — какой это случай, видно по самому снимку, поэтому проверяются оба словаря.
   * Строки блока копируются, а не берутся ссылкой: снимок `initial` сравнивается
   * с состоянием и меняться не должен.
   */
  const applyEntryFrom = (key: string, snapshot: FormState) => {
    setState((current) => ({
      scalars:
        key in snapshot.scalars
          ? { ...current.scalars, [key]: snapshot.scalars[key] }
          : current.scalars,
      blocks:
        key in snapshot.blocks
          ? {
              ...current.blocks,
              [key]: {
                rows: (snapshot.blocks[key]?.rows ?? []).map((row) => ({
                  ...row,
                  cells: { ...row.cells },
                })),
              },
            }
          : current.blocks,
    }));
  };

  /**
   * Переключить источник показателя или блока.
   *
   * Выбор хранит сервер (`source_mode`, ключ «показатель × КАТО × год»), поэтому
   * запрос уходит сразу, не дожидаясь «Сохранить»: он определяет, что оператор
   * может править прямо сейчас. Интерфейс переключается оптимистично, а на отказе
   * откатывается целиком — и тумблер, и значение: иначе форма показывала бы
   * состояние, которого на сервере нет.
   *
   * Возврат на источник ВОЗВРАЩАЕТ И ЗНАЧЕНИЕ. То, что оператор успел набрать
   * вручную, относится к слою ручного ввода, а под подписью «Мониторинг СНП»
   * должно стоять значение мониторинга — иначе в заблокированном поле остаётся
   * число, которого нет ни в одном источнике.
   */
  const toggleManual = (key: string, manual: boolean) => {
    if (kato === null) return;
    const previousKeys = manualKeys;
    const previousState = state;
    const next = new Set(previousKeys);
    if (manual) next.add(key);
    else next.delete(key);
    setManualKeys(next);
    if (!manual) applyEntryFrom(key, initial);

    void setSourceMode(kato, year, key, manual ? 'manual' : 'source').catch(() => {
      setManualKeys(previousKeys);
      if (!manual) applyEntryFrom(key, previousState);
      toast.error(t('adminInput.sourceFailed'));
    });
  };

  // --- фильтрация -------------------------------------------------------
  const normalizedQuery = query.trim().toLowerCase();
  const filtering = normalizedQuery !== '' || emptyOnly;

  const filterSection = useCallback(
    (section: PassportSection): PassportSection => {
      const matches = (ru: string, kz: string) =>
        normalizedQuery === ''
        || pick(ru, kz, lang).toLowerCase().includes(normalizedQuery);
      return {
        ...section,
        fields: section.fields.filter(
          (field) =>
            matches(field.label_ru, field.label_kz)
            && (!emptyOnly || (state.scalars[field.key] ?? '') === ''),
        ),
        blocks: section.blocks.filter(
          (block) =>
            matches(block.label_ru, block.label_kz)
            && (!emptyOnly || (state.blocks[block.id]?.rows.length ?? 0) === 0),
        ),
      };
    },
    [normalizedQuery, emptyOnly, lang, state],
  );

  const visible = useMemo(() => {
    if (form === null) return [];
    const indexed = form.sections.map((section, index) => ({ section, index }));
    const chosen = filtering
      ? indexed
      : indexed.filter((entry) => entry.index === activeSection);
    return chosen
      .map((entry) => ({ index: entry.index, section: filterSection(entry.section) }))
      .filter((entry) => entry.section.fields.length > 0 || entry.section.blocks.length > 0);
  }, [form, filtering, activeSection, filterSection]);

  // --- сохранение -------------------------------------------------------
  const handleSave = async () => {
    if (form === null || kato === null) return;
    setSaving(true);
    try {
      const result = await savePassportForm(toSaveRequest(form, state, initial));
      toast.success(
        t('adminInput.saved', { count: result.saved_fields + result.saved_blocks }),
      );
      await reload(kato, year);
    } catch {
      toast.error(t('adminInput.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // --- ветки доступа ----------------------------------------------------
  if (user === null) {
    return <p data-testid="input-data-loading">{t('common.loading')}</p>;
  }
  if (!canEdit) {
    return (
      <p className="p-5 text-sm text-text-muted" data-testid="input-data-forbidden">
        {t('adminInput.forbidden')}
      </p>
    );
  }

  const katoOptions = flatten(katoItems);
  const yearOptions = Array.from({ length: YEARS_BACK + 2 }, (_, i) => currentYear + 1 - i);
  const changedBlockIds = new Set(changedBlocks(state, initial));

  const fieldLabels = {
    source: t('adminInput.sourceLabel'),
    sourceSnp: t('adminInput.sourceSnp'),
    sourceManual: t('adminInput.sourceManual'),
  };
  const blockLabels = {
    ...fieldLabels,
    addRow: t('adminInput.addRow'),
    removeRow: t('adminInput.removeRow'),
    empty: t('adminInput.blockEmpty'),
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="input-data">
      <div className="flex flex-wrap items-end gap-4 border-b border-surface-border bg-white px-5 py-4">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold">{t('adminInput.title')}</h1>
          <p className="text-sm text-text-muted">{t('adminInput.subtitle')}</p>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('adminInput.territory')}</span>
          <select
            value={kato ?? ''}
            data-testid="input-data-kato"
            onChange={(event) => setKato(Number(event.target.value))}
            className="h-9 max-w-xs rounded-md border border-surface-border px-3"
          >
            {katoOptions.map(({ item, depth }) => (
              <option key={item.kato} value={item.kato}>
                {`${'  '.repeat(depth)}${lang === 'kz' ? item.name_kz : item.name_ru}`}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('adminInput.year')}</span>
          <select
            value={year}
            data-testid="input-data-year"
            onChange={(event) => setYear(Number(event.target.value))}
            className="h-9 rounded-md border border-surface-border px-3 tabular-nums"
          >
            {yearOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('adminInput.search')}</span>
          <span className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-text-muted" />
            <Input
              value={query}
              data-testid="input-data-search"
              placeholder={t('adminInput.searchPlaceholder')}
              onChange={(event) => setQuery(event.target.value)}
              className="h-9 w-64 pl-8"
            />
          </span>
        </label>

        <label className="flex h-9 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={emptyOnly}
            data-testid="input-data-empty-only"
            onChange={(event) => setEmptyOnly(event.target.checked)}
            className="size-4"
          />
          {t('adminInput.emptyOnly')}
        </label>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* На планшете 64 rem-колонки навигации не остаётся ширины под
            форму — до lg колонка уже. */}
        <nav className="w-52 shrink-0 overflow-y-auto border-r border-surface-border bg-white py-2 lg:w-64">
          {(form?.sections ?? []).map((section, index) => {
            const { filled, total } =
              form !== null
                ? sectionProgress(form, state, index)
                : { filled: 0, total: 0 };
            const percent = total === 0 ? null : Math.round((filled / total) * 100);
            return (
              <button
                // Ключ — позиция, а не заголовок: сервер склеивает одноимённые
                // разделы, но React не должен молча ломаться, если совпадение
                // когда-нибудь вернётся (одинаковые ключи → состояние не у того
                // элемента).
                key={index}
                type="button"
                data-testid={`section-${String(index)}`}
                onClick={() => setActiveSection(index)}
                className={cn(
                  'flex w-full flex-col gap-1 px-4 py-2.5 text-left text-sm transition-colors',
                  index === activeSection && !filtering
                    ? 'bg-brand-50 font-semibold text-brand-700'
                    : 'hover:bg-surface-muted',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    {pick(section.title_ru, section.title_kz, lang)}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-text-muted">
                    {percent === null ? '—' : `${String(percent)}%`}
                  </span>
                </span>
                {percent !== null && (
                  <span className="h-1 w-full overflow-hidden rounded-full bg-surface-muted">
                    <span
                      className="block h-full bg-brand-500"
                      style={{ width: `${String(percent)}%` }}
                    />
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 overflow-y-auto p-5">
          {kato !== null && !filtering && (
            <div className="mb-5">
              <TemplatePanel
                kato={kato}
                year={year}
                lang={lang}
                onApplied={(message) => {
                  toast.success(message);
                  void reload(kato, year);
                }}
              />
            </div>
          )}

          {loading ? (
            <div className="space-y-3" data-testid="input-data-skeleton">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : failed ? (
            <p className="text-sm text-text-muted" data-testid="input-data-failed">
              {t('common.dataUnavailable')}
            </p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-text-muted" data-testid="input-data-nothing">
              {t('adminInput.nothingFound')}
            </p>
          ) : (
            <div className="space-y-6">
              {visible.map(({ section, index }) => (
                <section key={index} className="space-y-3">
                  {filtering && (
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                      {pick(section.title_ru, section.title_kz, lang)}
                    </h2>
                  )}
                  {section.fields
                    .filter(isImageField)
                    .map((field) => (
                      <AkimPhotoCard
                        key={field.key}
                        kato={kato ?? 0}
                        year={year}
                        lang={lang}
                        field={field}
                        onUploaded={(message) => {
                          toast.success(message);
                          if (kato !== null) void reload(kato, year);
                        }}
                      />
                    ))}
                  <FieldsCard
                    // В режиме поиска заголовок раздела уже стоит над карточками
                    // (`<h2>` выше) — второй раз он бы просто дублировался.
                    title={filtering ? '' : pick(section.title_ru, section.title_kz, lang)}
                    fields={section.fields.filter(
                      (field) => !isImageField(field),
                    )}
                    lang={lang}
                    values={state.scalars}
                    initial={initial.scalars}
                    onChange={setScalar}
                    manualKeys={manualKeys}
                    onToggleManual={toggleManual}
                    labels={fieldLabels}
                  />
                  {section.blocks.map((block) => (
                    <BlockCard
                      key={block.id}
                      block={block}
                      lang={lang}
                      rows={state.blocks[block.id]?.rows ?? []}
                      changed={changedBlockIds.has(block.id)}
                      onCellChange={(uid, column, value) =>
                        setCell(block.id, uid, column, value)
                      }
                      onAddRow={() => addRow(block.id)}
                      onRemoveRow={(uid) => removeRow(block.id, uid)}
                      manual={!block.snp_source || manualKeys.has(block.id)}
                      onToggleManual={(manual) => toggleManual(block.id, manual)}
                      labels={blockLabels}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-surface-border bg-white px-5 py-3">
        <span className="text-sm text-text-muted" data-testid="input-data-dirty">
          {dirty === 0
            ? t('adminInput.noChanges')
            : t('adminInput.changes', { count: dirty })}
        </span>
        <span className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={dirty === 0 || saving}
            onClick={() => {
              if (form !== null) setState(buildState(form));
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={dirty === 0 || saving}
            data-testid="input-data-save"
            onClick={() => void handleSave()}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Check className="mr-2 size-4" />
            )}
            {t('common.save')}
          </Button>
        </span>
      </div>
    </div>
  );
};
